import type { Edge } from '@xyflow/react';
import type { WorkflowGraph, WorkflowNode, WorkflowNodeData, WorkflowNodeKind } from './types';

// Seed graphs for first run / when the DB is empty or holds stale legacy rows.
// Studio shape: nodes carry position + data.kind; edges are first-class.

function n(
  id: string,
  kind: WorkflowNodeKind,
  x: number,
  y: number,
  data: Partial<WorkflowNodeData>,
): WorkflowNode {
  return { id, type: 'workflow', position: { x, y }, data: { kind, ...data } as WorkflowNodeData };
}

function e(source: string, target: string): Edge {
  return { id: `${source}->${target}`, source, target };
}

const reviewCode: WorkflowGraph = {
  id: 'flow-review-code',
  name: 'Automated Code Review Flow',
  description: 'PR webhook → prompt + build → AI review → comment.',
  iconKey: 'webhook', iconClass: 'w-4 h-4 text-orange-600', category: 'PR Automation',
  nodes: [
    n('rc-webhook', 'trigger', 0, 140, { label: 'GitHub Webhook', iconKey: 'webhook', iconClass: 'w-4 h-4 text-orange-600',
      description: 'Receives pull_request events.', params: { url: '/api/webhook/github', events: 'pull_request' } }),
    n('rc-prompt', 'prompt', 280, 40, { label: 'Review Rules', iconKey: 'layers', iconClass: 'w-4 h-4 text-blue-600',
      description: 'System instructions + quality target.', params: { role: 'Senior Security Architect', qualityTargetScore: 85 } }),
    n('rc-build', 'tool', 280, 240, { label: 'Build Check', iconKey: 'code', iconClass: 'w-4 h-4 text-emerald-600',
      description: 'Compiles in a sandbox to verify syntax.', executor: 'shell', params: { command: 'npm run build', timeoutMs: 8000 } }),
    n('rc-ai', 'aiReview', 560, 140, { label: 'AI Review', iconKey: 'bot', iconClass: 'w-4 h-4 text-purple-600',
      description: 'Analyses diffs and proposes fixes.', executor: 'claude-review', params: { model: 'MiniMax-M3', temperature: 0.1 } }),
    n('rc-out', 'output', 840, 140, { label: 'PR Comment', iconKey: 'github', iconClass: 'w-4 h-4 text-slate-800',
      description: 'Posts inline review to the PR.', executor: 'gh-comment', params: { addLabels: 'ai-reviewed', autoApprove: false } }),
  ],
  edges: [e('rc-webhook', 'rc-prompt'), e('rc-webhook', 'rc-build'), e('rc-prompt', 'rc-ai'), e('rc-build', 'rc-ai'), e('rc-ai', 'rc-out')],
};

const dailyScan: WorkflowGraph = {
  id: 'flow-daily-scan',
  name: 'Daily Security Scan Flow',
  description: 'Daily cron → OWASP rules + secret scan → AI → Slack.',
  iconKey: 'database', iconClass: 'w-4 h-4 text-blue-600', category: 'SecOps Audit',
  nodes: [
    n('ds-cron', 'schedule', 0, 140, { label: 'Daily Cron', iconKey: 'clock', iconClass: 'w-4 h-4 text-blue-600',
      description: 'Triggers at midnight daily.', params: { cron: '0 0 * * *', timezone: 'UTC' } }),
    n('ds-rules', 'prompt', 280, 40, { label: 'OWASP Rules', iconKey: 'layers', iconClass: 'w-4 h-4 text-blue-600',
      description: 'OWASP Top-10 analysis rules.', params: { ruleset: 'OWASP-Top-10', strictness: 'critical' } }),
    n('ds-scan', 'tool', 280, 240, { label: 'Secret Scanner', iconKey: 'code', iconClass: 'w-4 h-4 text-emerald-600',
      description: 'Scans for leaked API keys.', executor: 'shell', params: { command: 'secret-scan', entropyThreshold: 4.5 } }),
    n('ds-ai', 'aiReview', 560, 140, { label: 'Security AI', iconKey: 'bot', iconClass: 'w-4 h-4 text-purple-600',
      description: 'Identifies SQLi / XSS / logic risks.', executor: 'claude-review', params: { model: 'MiniMax-M3', temperature: 0 } }),
    n('ds-notify', 'notify', 840, 140, { label: 'Slack Alert', iconKey: 'messageSquare', iconClass: 'w-4 h-4 text-orange-500',
      description: 'Posts summary to #security.', executor: 'notify', params: { channel: 'slack', mentionOnFail: '@security-team' } }),
  ],
  edges: [e('ds-cron', 'ds-rules'), e('ds-cron', 'ds-scan'), e('ds-rules', 'ds-ai'), e('ds-scan', 'ds-ai'), e('ds-ai', 'ds-notify')],
};

const autoPatch: WorkflowGraph = {
  id: 'flow-auto-patch',
  name: 'Self-Healing CI Flow',
  description: 'CI fail → patch prompt + sandbox → AI patch → commit.',
  iconKey: 'github', iconClass: 'w-4 h-4 text-purple-600', category: 'Self-Healing CI/CD',
  nodes: [
    n('ap-fail', 'trigger', 0, 140, { label: 'CI Fail Trigger', iconKey: 'webhook', iconClass: 'w-4 h-4 text-rose-600',
      description: 'Fires when the build/test pipeline crashes.', params: { url: '/api/webhook/ci', events: 'check_run' } }),
    n('ap-prompt', 'prompt', 280, 40, { label: 'Patch Rules', iconKey: 'layers', iconClass: 'w-4 h-4 text-blue-600',
      description: 'Syntax repair directives.', params: { styleGuide: 'Airbnb ES6', strictNullChecks: true } }),
    n('ap-sandbox', 'tool', 280, 240, { label: 'Test Sandbox', iconKey: 'code', iconClass: 'w-4 h-4 text-emerald-600',
      description: 'Runs the patched build.', executor: 'shell', params: { command: 'npm test', timeoutMs: 30000 } }),
    n('ap-ai', 'aiReview', 560, 140, { label: 'Patch AI', iconKey: 'bot', iconClass: 'w-4 h-4 text-purple-600',
      description: 'Generates the syntax patch.', executor: 'claude-review', params: { model: 'MiniMax-M3', temperature: 0.15 } }),
    n('ap-commit', 'tool', 840, 140, { label: 'Git Commit', iconKey: 'github', iconClass: 'w-4 h-4 text-slate-800',
      description: 'Commits + pushes the repair.', executor: 'shell', params: { command: 'gh commit + push', signCommits: true } }),
  ],
  edges: [e('ap-fail', 'ap-prompt'), e('ap-fail', 'ap-sandbox'), e('ap-prompt', 'ap-ai'), e('ap-sandbox', 'ap-ai'), e('ap-ai', 'ap-commit')],
};

const sbom: WorkflowGraph = {
  id: 'flow-sbom-compliance',
  name: 'SBOM & Licence Scan Flow',
  description: 'package.json change → licence audit + CVE scan → report.',
  iconKey: 'database', iconClass: 'w-4 h-4 text-emerald-600', category: 'Licence Compliance',
  nodes: [
    n('sb-trigger', 'trigger', 0, 140, { label: 'SBOM Trigger', iconKey: 'database', iconClass: 'w-4 h-4 text-emerald-600',
      description: 'Monitors lockfile changes.', params: { url: '/api/webhook/github', events: 'push' } }),
    n('sb-licence', 'prompt', 280, 40, { label: 'Licence Auditor', iconKey: 'layers', iconClass: 'w-4 h-4 text-blue-600',
      description: 'Flags copyleft GPL libs.', params: { allowedLicenses: 'MIT, Apache-2.0', rejectGPL: true } }),
    n('sb-cve', 'sbom', 280, 240, { label: 'CVE Audit', iconKey: 'database', iconClass: 'w-4 h-4 text-teal-600',
      description: 'Audits deps vs CVE DBs.', executor: 'sbom', params: { minSeverity: 'high' } }),
    n('sb-ai', 'aiReview', 560, 140, { label: 'Compliance AI', iconKey: 'bot', iconClass: 'w-4 h-4 text-purple-600',
      description: 'Summarises the SBOM.', executor: 'claude-review', params: { model: 'MiniMax-M3', temperature: 0.1 } }),
    n('sb-out', 'output', 840, 140, { label: 'SBOM Report', iconKey: 'github', iconClass: 'w-4 h-4 text-slate-800',
      description: 'Writes CycloneDX to the vault.', executor: 'gh-comment', params: { format: 'CycloneDX JSON', triggerAlertOnHighRisk: true } }),
  ],
  edges: [e('sb-trigger', 'sb-licence'), e('sb-trigger', 'sb-cve'), e('sb-licence', 'sb-ai'), e('sb-cve', 'sb-ai'), e('sb-ai', 'sb-out')],
};

const complexity: WorkflowGraph = {
  id: 'flow-complexity-sweeper',
  name: 'Weekly Quality Sweep Flow',
  description: 'Weekly cron → complexity rules + lint → AI refactor → notify.',
  iconKey: 'code', iconClass: 'w-4 h-4 text-amber-600', category: 'Quality Assurance',
  nodes: [
    n('cs-cron', 'schedule', 0, 140, { label: 'Weekly Cron', iconKey: 'clock', iconClass: 'w-4 h-4 text-amber-600',
      description: 'Monday 08:00.', params: { cron: '0 8 * * 1', timezone: 'UTC' } }),
    n('cs-rules', 'prompt', 280, 40, { label: 'Complexity Rules', iconKey: 'layers', iconClass: 'w-4 h-4 text-blue-600',
      description: 'Cyclomatic complexity thresholds.', params: { maxCyclomaticComplexity: 15, maxFunctionLines: 60 } }),
    n('cs-lint', 'tool', 280, 240, { label: 'ESLint Sweeper', iconKey: 'code', iconClass: 'w-4 h-4 text-emerald-600',
      description: 'Static analysis for code smells.', executor: 'shell', params: { command: 'eslint .', ruleset: 'recommended' } }),
    n('cs-ai', 'aiReview', 560, 140, { label: 'Refactor AI', iconKey: 'bot', iconClass: 'w-4 h-4 text-purple-600',
      description: 'Restructures complex blocks.', executor: 'claude-review', params: { model: 'MiniMax-M3', temperature: 0.1 } }),
    n('cs-notify', 'notify', 840, 140, { label: 'Email Alert', iconKey: 'messageSquare', iconClass: 'w-4 h-4 text-blue-500',
      description: 'Emails refactor suggestions.', executor: 'notify', params: { channel: 'email', highPriorityOnly: true } }),
  ],
  edges: [e('cs-cron', 'cs-rules'), e('cs-cron', 'cs-lint'), e('cs-rules', 'cs-ai'), e('cs-lint', 'cs-ai'), e('cs-ai', 'cs-notify')],
};

export const DEFAULT_GRAPHS: WorkflowGraph[] = [reviewCode, dailyScan, autoPatch, sbom, complexity];
